/**
 * iter232 — Global auto-tooltip for icon-only action buttons.
 *
 * Zero-refactor design:
 *   1. Mounted ONCE in App.js.
 *   2. Uses event delegation on `mouseover` / `mouseout` / `mousemove`.
 *   3. Activates for icon-only <button> / <a> elements that carry a `title`
 *      attribute (already ubiquitous across the ERP — Printer/Eye/Download/
 *      Edit/Trash all have title="Yazdır"/"Önizle"/...).
 *   4. When the trigger is first observed, moves its `title` → `data-tip`
 *      to suppress the native browser tooltip. Original text is preserved
 *      for accessibility via `aria-label` fallback.
 *   5. After a ~2000ms hover, renders a small styled floating tooltip
 *      near the element. Auto-flips at screen edges.
 *   6. Mouseleave or scroll cancels the pending timer & removes tooltip.
 *
 * Excluded (non-icon controls keep native behaviour):
 *   - Buttons with textual content (>= 3 chars)
 *   - Elements with `data-no-tip`
 *   - Elements inside form/input labels / disabled / aria-hidden
 *
 * Mobile: no hover, so no tooltip is shown — native touch behaviour unchanged.
 */
import { useEffect } from "react";

const HOVER_DELAY_MS = 1000;
const TIP_ID = "erp-auto-tooltip";
const TIP_MAX_W = 220;

function isIconOnly(el) {
  if (!el) return false;
  if (el.hasAttribute("data-no-tip")) return false;
  if (el.getAttribute("aria-hidden") === "true") return false;
  if (el.disabled) return false;
  const tag = el.tagName;
  if (tag !== "BUTTON" && tag !== "A") return false;
  // Text content check — icon-only means little to no readable text.
  const text = (el.textContent || "").trim();
  // Allow short badges like "0" but require < 3 chars for icon-only.
  if (text.length >= 3) return false;
  // Must contain an SVG (icon) OR a single <i> element.
  const hasIcon = el.querySelector("svg, i");
  if (!hasIcon) return false;
  return true;
}

function ensureTipEl() {
  let el = document.getElementById(TIP_ID);
  if (el) return el;
  el = document.createElement("div");
  el.id = TIP_ID;
  el.setAttribute("role", "tooltip");
  el.style.cssText = [
    "position:fixed",
    "z-index:9999",
    "pointer-events:none",
    "opacity:0",
    "transform:translateY(2px)",
    "transition:opacity 120ms ease, transform 120ms ease",
    `max-width:${TIP_MAX_W}px`,
    "padding:5px 9px",
    "border-radius:6px",
    "background:rgba(15,23,42,0.94)",   // slate-900/94
    "color:#f8fafc",                     // slate-50
    "font-size:11.5px",
    "font-weight:500",
    "line-height:1.35",
    "letter-spacing:0.01em",
    "box-shadow:0 4px 16px rgba(0,0,0,0.18)",
    "white-space:normal",
    "word-break:break-word",
    "font-family:'Manrope','Inter',ui-sans-serif,system-ui,sans-serif",
  ].join(";");
  document.body.appendChild(el);
  return el;
}

function positionTip(tipEl, target) {
  const rect = target.getBoundingClientRect();
  const tipRect = tipEl.getBoundingClientRect();
  const vw = window.innerWidth;
  const vh = window.innerHeight;
  const pad = 6;

  // Prefer below the trigger.
  let top = rect.bottom + pad;
  let left = rect.left + rect.width / 2 - tipRect.width / 2;

  // Flip above if would overflow bottom.
  if (top + tipRect.height > vh - 4) top = rect.top - tipRect.height - pad;
  // Clamp horizontally.
  if (left < 4) left = 4;
  if (left + tipRect.width > vw - 4) left = vw - tipRect.width - 4;

  tipEl.style.top = `${Math.max(4, top)}px`;
  tipEl.style.left = `${left}px`;
}

function showTip(target) {
  // Extract the tip text: prefer data-tip (already migrated), fallback title.
  let text = target.getAttribute("data-tip");
  if (!text) {
    text = target.getAttribute("title") || "";
    if (text) {
      target.setAttribute("data-tip", text);
      // Aria-label preserved for a11y if not already present.
      if (!target.hasAttribute("aria-label")) {
        target.setAttribute("aria-label", text);
      }
      target.removeAttribute("title");
    }
  }
  if (!text) return;
  const tipEl = ensureTipEl();
  tipEl.textContent = text;
  // Two-pass positioning: paint offscreen first so we can measure.
  tipEl.style.left = "-9999px";
  tipEl.style.top = "-9999px";
  tipEl.style.opacity = "0";
  // Force layout, then position.
  requestAnimationFrame(() => {
    positionTip(tipEl, target);
    tipEl.style.opacity = "1";
    tipEl.style.transform = "translateY(0)";
  });
}

function hideTip() {
  const tipEl = document.getElementById(TIP_ID);
  if (!tipEl) return;
  tipEl.style.opacity = "0";
  tipEl.style.transform = "translateY(2px)";
}

export default function AutoTooltip() {
  useEffect(() => {
    let timerId = null;
    let currentTarget = null;

    const clear = () => {
      if (timerId) { clearTimeout(timerId); timerId = null; }
      currentTarget = null;
      hideTip();
    };

    const onOver = (e) => {
      // Walk up to find the nearest button/anchor.
      const el = e.target.closest && e.target.closest("button,a");
      if (!el || !isIconOnly(el)) return;
      if (!el.getAttribute("title") && !el.getAttribute("data-tip")) return;
      if (el === currentTarget) return;
      clear();
      // iter236 — Migrate `title` → `data-tip` IMMEDIATELY on first hover so
      // the browser's native tooltip never fires. Previously we did this
      // only inside showTip() (after the delay), which meant the native
      // tooltip appeared alongside our custom one for ~500ms.
      const t = el.getAttribute("title");
      if (t) {
        el.setAttribute("data-tip", t);
        if (!el.hasAttribute("aria-label")) el.setAttribute("aria-label", t);
        el.removeAttribute("title");
      }
      currentTarget = el;
      timerId = setTimeout(() => {
        if (currentTarget === el && document.body.contains(el)) {
          showTip(el);
        }
      }, HOVER_DELAY_MS);
    };

    const onOut = (e) => {
      const el = e.target.closest && e.target.closest("button,a");
      if (!el || el !== currentTarget) return;
      // Ignore mouseover→mouseout ping-pong inside the same element.
      const related = e.relatedTarget;
      if (related && el.contains(related)) return;
      clear();
    };

    const onScrollOrClick = () => clear();

    document.addEventListener("mouseover", onOver, true);
    document.addEventListener("mouseout", onOut, true);
    document.addEventListener("scroll", onScrollOrClick, true);
    document.addEventListener("mousedown", onScrollOrClick, true);
    window.addEventListener("blur", onScrollOrClick);

    return () => {
      clear();
      document.removeEventListener("mouseover", onOver, true);
      document.removeEventListener("mouseout", onOut, true);
      document.removeEventListener("scroll", onScrollOrClick, true);
      document.removeEventListener("mousedown", onScrollOrClick, true);
      window.removeEventListener("blur", onScrollOrClick);
    };
  }, []);
  return null;
}
